import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import { formatMoney, formatShortDate } from '@/proposals/lib/money';
import {
  PAYMENT_RAIL,
  PAYMENT_RAIL_LABELS,
  RECEIVABLE_FILTER,
  RECEIVABLE_SORT_FIELDS,
  RECEIVABLE_STATUS_PILLS,
  listReceivables,
  retryPaymentSync,
  type ReceivableFilter,
  type ReceivableRow,
} from '@/lib/receivables';
import { PaymentLinkDrawer } from '@/components/PaymentLinkDrawer';

const PAGE_SIZE = 25;
const FILTER_PARAM = 'filter';

/** The tabs, in the order they show. */
const FILTERS: readonly { readonly key: ReceivableFilter; readonly label: string }[] = [
  { key: RECEIVABLE_FILTER.outstanding, label: 'Outstanding' },
  { key: RECEIVABLE_FILTER.overdue, label: 'Overdue' },
  { key: RECEIVABLE_FILTER.paid, label: 'Paid' },
  { key: RECEIVABLE_FILTER.all, label: 'All' },
];

/** What an empty tab says. */
const EMPTY_MESSAGES: Readonly<Record<ReceivableFilter, string>> = {
  [RECEIVABLE_FILTER.outstanding]: 'Contracted events with a balance still owed show up here.',
  [RECEIVABLE_FILTER.overdue]: 'Nothing is past due. Balances show up here the day after their due date.',
  [RECEIVABLE_FILTER.paid]: 'Contracted events that are paid in full show up here.',
  [RECEIVABLE_FILTER.all]: 'Events from the last year and upcoming show up here once they reach Awaiting Signature with a contracted amount.',
};

const SORT_KEYS = Object.values(RECEIVABLE_SORT_FIELDS);
/** Soonest due first: what needs chasing next. */
const DEFAULT_SORT = { key: RECEIVABLE_SORT_FIELDS.dueDate, direction: 'asc' as const };

function isFilter(value: string | null): value is ReceivableFilter {
  return FILTERS.some((filter) => filter.key === value);
}

/**
 * Retry for a row's Stripe payments that didn't reach Salesforce. Shown only
 * when there are some; each retry is idempotent on the Salesforce side.
 */
function SalesforceSyncCell({ row }: { readonly row: ReceivableRow }): React.ReactElement {
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: async () => {
      for (const id of row.unsyncedPaymentIds) await retryPaymentSync(id);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['receivables'] }),
  });
  // Nothing to flag: Sertifi payments are Salesforce's own, and synced Stripe
  // payments need no action. The column only speaks up when one needs a retry.
  if (row.unsyncedPaymentIds.length === 0) return <span className="text-portal-muted">—</span>;
  const count = row.unsyncedPaymentIds.length;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Pill tone="warning">{count === 1 ? '1 payment not in Salesforce' : `${count} payments not in Salesforce`}</Pill>
      <Button variant="secondary" size="compact" onClick={() => retry.mutate()} disabled={retry.isPending}>
        {retry.isPending ? 'Retrying…' : 'Retry'}
      </Button>
      {retry.isError ? <span className="text-portal-micro text-portal-danger">{errorMessage(retry.error)}</span> : null}
    </span>
  );
}

/**
 * Sales → Payments: what every contracted event is owed, paid and overdue,
 * across Sertifi and BCEcloud's Stripe payments while both run. Salesforce is
 * the record of what's been paid; a Stripe payment it hasn't synced yet is
 * added on top, and one that failed to sync can be retried here. Gated by the
 * `manage_payments` permission at the route.
 *
 * @returns The payments list.
 */
export function PaymentsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const requested = params.get(FILTER_PARAM);
  const filter: ReceivableFilter = isFilter(requested) ? requested : RECEIVABLE_FILTER.outstanding;
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const activeSort = sort ?? DEFAULT_SORT;

  const list = useQuery({
    queryKey: ['receivables', filter, debounced, activeSort.key, activeSort.direction, page],
    queryFn: ({ signal }) =>
      listReceivables(
        {
          filter,
          ...(debounced ? { search: debounced } : {}),
          sort: activeSort.key,
          dir: activeSort.direction,
          page,
          limit: PAGE_SIZE,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const rows = list.data?.items ?? [];
  const [linkFor, setLinkFor] = useState<ReceivableRow | null>(null);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        title="Payments"
        subtitle="What contracted events from the last year and upcoming are owed, paid and overdue, whether the client pays through Sertifi or BCEcloud."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search payments"
          placeholder="Contract #, client, event or program"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <SegmentedControl<ReceivableFilter>
          label="Show"
          value={filter}
          onChange={(next) => {
            const nextParams = new URLSearchParams(params);
            nextParams.set(FILTER_PARAM, next);
            nextParams.delete('page');
            setParams(nextParams, { replace: true });
          }}
          options={FILTERS.map((f) => ({ key: f.key, label: f.label }))}
        />
      </div>

      {list.isPending ? (
        <SkeletonTable />
      ) : list.isError ? (
        <ErrorNotice title="Payments unavailable" message={errorMessage(list.error)} />
      ) : rows.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState title="No payments match" message="Try a different search, or another tab." />
          ) : (
            <EmptyState title="Nothing here" message={EMPTY_MESSAGES[filter]} />
          )}
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: 'Event', sortKey: RECEIVABLE_SORT_FIELDS.client },
              { label: 'Event date', sortKey: RECEIVABLE_SORT_FIELDS.eventDate },
              { label: 'Contracted', sortKey: RECEIVABLE_SORT_FIELDS.contracted },
              { label: 'Paid' },
              { label: 'Owed', sortKey: RECEIVABLE_SORT_FIELDS.owed },
              { label: 'Due', sortKey: RECEIVABLE_SORT_FIELDS.dueDate },
              { label: 'Status' },
              { label: 'Pays through' },
              { label: 'Salesforce' },
              { label: 'Payment link' },
            ]}
            rightAlignedHeaders={[2, 3, 4]}
            sort={activeSort}
            onSort={onSort}
            minWidth={1240}
          >
            {rows.map((row) => {
              const pill = RECEIVABLE_STATUS_PILLS[row.status];
              return (
                <tr key={row.eventId} className="border-t border-portal-line align-middle">
                  <td className="p-3 text-portal-small">
                    <Link to={`/events/${row.eventId}`} className="font-bold text-primary hover:underline">
                      {row.contractNumber ? `#${row.contractNumber}` : (row.eventName ?? 'Event')}
                    </Link>
                    <span className="block text-portal-micro text-portal-muted">{row.accountName ?? '—'}</span>
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{formatShortDate(row.eventDate)}</td>
                  <td className="p-3 text-portal-small text-right whitespace-nowrap">{formatMoney(row.contracted)}</td>
                  <td className="p-3 text-portal-small text-right text-portal-muted whitespace-nowrap">{formatMoney(row.paid)}</td>
                  <td className="p-3 text-portal-small text-right font-bold whitespace-nowrap">{formatMoney(row.owed)}</td>
                  <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{formatShortDate(row.dueDate)}</td>
                  <td className="p-3 text-portal-small whitespace-nowrap">
                    <Pill tone={pill.tone}>{pill.label}</Pill>
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted">{PAYMENT_RAIL_LABELS[row.rail]}</td>
                  <td className="p-3 text-portal-small">
                    <SalesforceSyncCell row={row} />
                  </td>
                  <td className="p-3 text-portal-small whitespace-nowrap">
                    {/* Links are for BCEcloud-contracted events; Sertifi events pay through Sertifi. */}
                    {row.rail === PAYMENT_RAIL.stripe && row.owed > 0 ? (
                      <Button variant="secondary" size="compact" onClick={() => setLinkFor(row)}>
                        Payment link
                      </Button>
                    ) : (
                      <span className="text-portal-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </DataTable>
          <Pagination
            page={list.data.pagination.page}
            totalPages={list.data.pagination.totalPages}
            onChange={setPage}
            summary={`${(list.data.pagination.page - 1) * PAGE_SIZE + 1}–${
              (list.data.pagination.page - 1) * PAGE_SIZE + rows.length
            } of ${list.data.pagination.total.toLocaleString()}`}
            className="mt-3"
          />
        </>
      )}
      {linkFor ? (
        <PaymentLinkDrawer
          open
          eventId={linkFor.eventId}
          title={[linkFor.contractNumber ? `#${linkFor.contractNumber}` : null, linkFor.accountName].filter(Boolean).join(' · ')}
          onClose={() => setLinkFor(null)}
        />
      ) : null}
    </section>
  );
}
